import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useConsulting } from '../../context/ConsultingContext';
import { useToast } from '../../context/ToastContext';
import { Header, Footer } from '../../components/layout';
import { FileText, Trash2, Calendar, Clock, Plus, Loader2, RefreshCw, X, ArrowRight } from 'lucide-react';
import { LatteButton, LatteSpinner, ConfirmationModal, MobileConfirmationModal } from '../../components/common';
import ConsultantSelector from '../../components/consultant/ConsultantSelector';
import { supabase } from '../../lib/supabase';
import { calculateResumeProgress } from '../../utils/progressSync';
import { trackEvent } from '../../utils/analytics';
import { trackSupabaseError, trackFeatureError } from '../../utils/errorTracking';

// 심플한 진행률 표시 컴포넌트
const SimpleProgressIndicator = ({ resumeData, className = "" }) => {
  const progress = calculateResumeProgress(resumeData);
  
  const getProgressText = (progress) => {
    if (progress === 100) return '완성';
    if (progress > 0) return `${progress}%`;
    return '0%';
  };

  const getProgressTextColor = (progress) => {
    if (progress === 100) return 'text-green-600 font-semibold';   // 연락처 완료 (100%): 초록색 (success)
    if (progress >= 20) return 'text-yellow-800 font-medium';      // 경력/교육/스킬/핵심역량 (20-99%): 라떼색
    return 'text-gray-500';                                       // 기본정보 (0-19%): 회색
  };

  return (
    <span className={`text-sm ${getProgressTextColor(progress)} ${className}`}>
      {getProgressText(progress)}
    </span>
  );
};

const DocumentsPage = () => {
  const { user, profile } = useAuth();
  const { startNewSession, loading: consultingLoading } = useConsulting();
  const { showSuccess, showError, showInfo } = useToast();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingIds, setDeletingIds] = useState(new Set());

  const [showNameModal, setShowNameModal] = useState(false);
  const [name, setName] = useState('');
  const [showConsultantSelector, setShowConsultantSelector] = useState(false);
  const [selectedConsultantData, setSelectedConsultantData] = useState(null);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    fetchDocuments();
  }, [user]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      
      const { data: sessions, error } = await supabase
        .from('consulting_sessions')
        .select(`
          id,
          name,
          progress_percentage,
          current_step,
          status,
          created_at,
          updated_at
        `)
        .eq('user_id', user.id)
        .is('deleted_at', null)  // 삭제되지 않은 세션만 조회
        .order('updated_at', { ascending: false });

      if (error) {
        trackSupabaseError('fetch', error, 'consulting_sessions');
        throw error;
      }

      if (!sessions || sessions.length === 0) {
        setDocuments([]);
        return;
      }

      const documentsWithResumeData = await Promise.all(
        sessions.map(async (session) => {
          const [resumeResult, portfolioResult] = await Promise.all([
            supabase
              .from('resumes')
              .select('*')  // 모든 필드 조회
              .eq('session_id', session.id)
              .eq('is_active', true)
              .limit(1),
            supabase
              .from('portfolios')
              .select('id, slug, status')
              .eq('session_id', session.id)
              .eq('user_id', user.id)
              .is('deleted_at', null)  // 삭제되지 않은 포트폴리오만 조회
              .in('status', ['published', 'draft'])
              .limit(1)
          ]);

          const { data: resumes, error: resumeError } = resumeResult;
          const { data: portfolios, error: portfolioError } = portfolioResult;

          if (resumeError) {
            console.error(`세션 ${session.id}의 이력서 데이터 조회 오류:`, resumeError);
            trackSupabaseError('fetch', resumeError, 'resumes');
          }
          if (portfolioError && portfolioError.code !== 'PGRST116') {
            console.error(`세션 ${session.id}의 포트폴리오 데이터 조회 오류:`, portfolioError.message, portfolioError.code, portfolioError);
            trackSupabaseError('fetch', portfolioError, 'portfolios');
          }

          const resumeData = resumes && resumes.length > 0 ? resumes[0] : null;
          const portfolioData = portfolios && portfolios.length > 0 ? portfolios[0] : null;
          const documentName = resumeData?.name || session.name || '이름 없음';

          // 🔧 진행률 계산을 위한 데이터 변환
          let currentProgress = session.progress_percentage || 0;
          let formattedResumeData = null;
          
          if (resumeData) {
            formattedResumeData = {
              name: resumeData.name,
              title: resumeData.title,
              summary: resumeData.summary,
              experiences: resumeData.experiences || [],
              education: resumeData.education || [],
              skills: resumeData.skills || [],
              keyStrengths: resumeData.key_strengths || [], // DB에서는 key_strengths로 저장됨
              phone: resumeData.phone,
              email: resumeData.email,
              contact_phone: resumeData.contact_phone,
              contact_email: resumeData.contact_email
            };
            
            // 실제 데이터 기반 진행률 계산
            const calculatedProgress = calculateResumeProgress(formattedResumeData);
            
            // DB 진행률과 계산된 진행률이 다르면 계산된 값을 표시만 하고 DB는 업데이트하지 않음
            if (Math.abs(calculatedProgress - currentProgress) > 5) {
              currentProgress = calculatedProgress;
              
              // 조회만 했을 때는 updated_at이 변경되지 않도록 DB 업데이트를 하지 않음
              // 실제 이력서 편집이 있을 때만 진행률이 업데이트되어야 함
              //console.log(`세션 ${session.id}: DB 진행률(${session.progress_percentage}%) vs 계산된 진행률(${calculatedProgress}%)`);
            }
          }

          return {
            id: session.id,
            type: 'resume',
            title: `${documentName}님의 이력서`,
            name: documentName,
            progress: currentProgress, // 실제 데이터 기반 진행률 사용
            step: session.current_step,
            status: session.status,
            created_at: session.created_at,
            updated_at: session.updated_at,
            resumeData: formattedResumeData,
            portfolioData: portfolioData,
            dbProgress: session.progress_percentage || 0 // 비교용
          };
        })
      );

      setDocuments(documentsWithResumeData);
      
    } catch (error) {
      console.error('문서 목록 조회 실패:', error);
      trackFeatureError('Documents', 'fetchDocuments', error);
      showError('문서 목록을 불러오는 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartNewConsulting = async () => {
    // 먼저 컨설턴트 선택 모달을 표시
    setShowConsultantSelector(true);
  };

  const handleConsultantSelect = async (consultantData) => {
    setSelectedConsultantData(consultantData);
    setShowConsultantSelector(false);
    
    // 프로필에서 이름이 있으면 바로 세션 시작, 없으면 이름 입력 모달 표시
    if (profile?.name) {
      try {
        await startNewSession(profile.name, consultantData);
        trackEvent.startConsulting(); // GA4 이벤트 추적
        navigate('/consulting');
      } catch (error) {
        console.error('세션 시작 실패:', error);
        showError('컨설팅 세션을 시작하는 중 오류가 발생했습니다.');
      }
    } else {
      setShowNameModal(true);
    }
  };

  const handleConsultantSelectorCancel = () => {
    setShowConsultantSelector(false);
    setSelectedConsultantData(null);
  };

  const handleNameSubmit = async () => {
    if (name.trim() && selectedConsultantData) {
      try {
        await startNewSession(name.trim(), selectedConsultantData);
        trackEvent.startConsulting(); // GA4 이벤트 추적
        navigate('/consulting');
        setShowNameModal(false);
        setName('');
        setSelectedConsultantData(null);
      } catch (error) {
        console.error('세션 시작 실패:', error);
        showError('컨설팅 세션을 시작하는 중 오류가 발생했습니다.');
      }
    }
  };

  const handleNameModalClose = () => {
    setShowNameModal(false);
    setName('');
    setSelectedConsultantData(null);
  };

  const handleView = (doc) => {
    trackEvent.viewDocument('resume', doc.id); // GA4 이벤트 추적
    navigate(`/resume/${doc.id}`);
  };

  const handleDelete = (doc) => {
    setDeleteTarget(doc);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    
    const doc = deleteTarget;
    setDeleteTarget(null);
    
    setDeletingIds(prev => new Set([...prev, doc.id]));

    try {
      // 1. 세션 존재 확인 및 소유권 검증
      const { data: sessionExists, error: checkError } = await supabase
        .from('consulting_sessions')
        .select('id, user_id, name, status')
        .eq('id', doc.id)
        .single();

      if (checkError) {
        if (checkError.code === 'PGRST116') {
          setDocuments(prev => prev.filter(d => d.id !== doc.id));
          showInfo('이미 삭제된 문서입니다.');
          return;
        }
        throw new Error(`세션 확인 실패: ${checkError.message}`);
      }

      if (sessionExists.user_id !== user.id) {
        throw new Error('이 문서를 삭제할 권한이 없습니다.');
      }

      // 2. 관련 데이터 개수 확인
      const [chatResult, resumeResult, aiLogResult] = await Promise.all([
        supabase.from('chat_messages').select('id', { count: 'exact' }).eq('session_id', doc.id),
        supabase.from('resumes').select('id', { count: 'exact' }).eq('session_id', doc.id),
        supabase.from('ai_logs').select('id', { count: 'exact' }).eq('session_id', doc.id)
      ]);

      const relatedCounts = {
        chatMessages: chatResult.count || 0,
        resumes: resumeResult.count || 0,
        aiLogs: aiLogResult.count || 0
      };

      // 3. 소프트 삭제로 변경 - 관련 데이터는 그대로 두고 세션만 삭제 표시
      // 컨설팅 세션을 소프트 삭제로 처리
      const { error: sessionError, count: sessionUpdatedCount } = await supabase
        .from('consulting_sessions')
        .update({ 
          deleted_at: new Date().toISOString(),
          status: 'abandoned'  // 상태도 함께 업데이트
        }, { count: 'exact' })
        .eq('id', doc.id)
        .eq('user_id', user.id)
        .is('deleted_at', null);  // 이미 삭제된 것은 제외

      if (sessionError) {
        throw new Error(`세션 삭제 실패: ${sessionError.message}`);
      }

      if (sessionUpdatedCount === 0) {
        throw new Error('세션 삭제에 실패했습니다. 이미 삭제되었거나 권한이 없습니다.');
      }

      // 5. UI에서 제거
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      
      // 6. 성공 메시지
      showSuccess('문서가 성공적으로 삭제되었습니다.');
      
      // GA4 이벤트 추적
      trackEvent.deleteDocument('resume', doc.id);
      
    } catch (error) {
      console.error('문서 삭제 실패:', error);
      
      let errorMessage = '문서 삭제 중 오류가 발생했습니다.';
      if (error.message) {
        errorMessage = `${errorMessage}: ${error.message}`;
      }
      
      showError(errorMessage);
      
      // 실패 시 UI 상태 복원을 위해 문서 목록 다시 가져오기
      await fetchDocuments();
      
    } finally {
      // 삭제 중 상태 해제
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(doc.id);
        return newSet;
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const filteredDocuments = documents;

  return (
    <div className="page-container mobile-fixed-layout">
      <Header title="이력서 목록" />
      
      <main className="page-content-lg">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-baseline gap-2">
                <h1 className="text-2xl font-semibold text-gray-900">
                  내 이력서 목록
                </h1>
                {filteredDocuments.length > 0 && !loading && (
                  <span className="text-sm text-gray-400">
                    ( {filteredDocuments.length} 건 )
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-sm mt-1">
                생성된 이력서를 관리하고 AI 컨설팅을 계속하세요
              </p>
            </div>
            
            <LatteButton
              onClick={handleStartNewConsulting}
              disabled={consultingLoading}
              size="lg"
              className="btn-gradient"
            >
              {consultingLoading ? (
                <LatteSpinner size="sm" />
              ) : (
                <Plus className="w-5 h-5" />
              )}
              {consultingLoading ? '시작 중...' : '새 컨설팅 시작'}
            </LatteButton>
          </div>
          <div className="mt-6 h-px bg-gray-200"></div>
        </div>



        {/* 문서 목록 */}
        {loading ? (
        <div className="card card-lg loading">
        <div className="flex flex-col items-center justify-center">
        <LatteSpinner size="md" className="mb-4" />
        <p className="text-gray-600">문서 목록을 불러오는 중...</p>
        </div>
        </div>
        ) : filteredDocuments.length === 0 ? (
        <div className="card empty-state">
        <FileText className="empty-state-icon" />
        <h3 className="empty-state-title">
        생성된 이력서 세션이 없습니다
        </h3>
        <p className="empty-state-description">
        AI 컨설팅을 통해 첫 번째 이력서를 만들어보세요
        </p>
        <LatteButton
        onClick={handleStartNewConsulting}
        disabled={consultingLoading}
        size="lg"
          className="btn-gradient btn-lg"
        >
        {consultingLoading ? (
          <LatteSpinner size="sm" />
        ) : (
          <Plus className="w-5 h-5" />
        )}
          {consultingLoading ? '시작 중...' : '새 컨설팅 시작하기'}
          </LatteButton>
          </div>
        ) : (
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
        {filteredDocuments.map((doc) => {
        const isDeleting = deletingIds.has(doc.id);
        
        return (
        <div
        key={doc.id}
        className={`card card-compact ${
            isDeleting ? 'loading' : 'hover:shadow-lg'
        }`}
        >
        {/* 카드 헤더 */}
        <div>
          <div 
            className="flex items-start justify-between mb-4 cursor-pointer"
            onClick={() => handleView(doc)}
          >
            <div className="flex-1 min-w-0">
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 truncate text-lg hover:text-amber-700 transition-colors">
                  {doc.title}
                </h3>
              </div>
              
              {/* 날짜 정보 */}
              <div className="space-y-1 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <span className="w-3.5 h-3.5 flex items-center justify-center text-xs font-bold text-gray-600">%</span>
                  <span className="font-semibold text-gray-700">진행률: <SimpleProgressIndicator resumeData={doc.resumeData} /></span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>생성: {formatDate(doc.created_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" />
                  <span>수정: {formatDate(doc.updated_at)}</span>
                </div>
              </div>
            </div>
            
            <LatteButton
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(doc);
              }}
              disabled={isDeleting}
              variant="danger-ghost"
              size="sm"
              title={isDeleting ? '삭제 중...' : '삭제'}
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </LatteButton>
          </div>

          {/* 액션 링크들 */}
          <div className="pt-4 border-t border-gray-100">
            <div className="flex items-center justify-center gap-2 text-sm whitespace-nowrap">
              <button
                onClick={() => navigate(`/consulting?session=${doc.id}`)}
                disabled={isDeleting}
                className="text-xs px-3 py-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? '삭제 중...' : 'AI 컨설팅 계속하기'}
              </button>
              
              <span className="text-gray-300">|</span>
              
              <button
                onClick={() => handleView(doc)}
                disabled={isDeleting}
                className="text-xs px-3 py-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                이력서
              </button>
              
              {doc.portfolioData && (
                <>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => window.open(`/portfolio/${doc.portfolioData.slug}`, '_blank', 'noopener,noreferrer')}
                    disabled={isDeleting}
                    className="text-xs px-3 py-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    웹페이지
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      
      <Footer />
      
      {/* 삭제 확인 모달 - 데스크탑/모바일 분기 */}
      {isMobile ? (
        <MobileConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteTarget(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="이력서 삭제"
          message={deleteTarget ? `"${deleteTarget.title}"을(를) 삭제하시겠습니까?\n\n⚠️ 이 작업은 되돌릴 수 없으며, 관련된 모든 데이터가 함께 삭제됩니다.` : ''}
          confirmText="삭제"
          cancelText="취소"
          type="danger"
        />
      ) : (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteTarget(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="이력서 삭제"
          message={deleteTarget ? `"${deleteTarget.title}"을(를) 삭제하시겠습니까?\n\n⚠️ 이 작업은 되돌릴 수 없으며, 관련된 모든 데이터가 함께 삭제됩니다.` : ''}
          confirmText="삭제"
          cancelText="취소"
          type="danger"
        />
      )}
      
      {/* 컨설턴트 선택 모달 */}
      {showConsultantSelector && (
        <ConsultantSelector
          onSelectConsultant={handleConsultantSelect}
          onCancel={handleConsultantSelectorCancel}
        />
      )}
      
      {/* 이름 입력 모달 */}
      {showNameModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">
                새 컨설팅 시작하기
              </h3>
              <button
                onClick={handleNameModalClose}
                className="modal-close-btn"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
              <label className="label">
                성함을 알려주세요
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleNameSubmit()}
                placeholder="예: 홍길동"
                className="input"
                autoFocus
              />
              <p className="form-help">입력하신 이름은 AI 컨설팅과 이력서 작성에 사용됩니다.</p>
              </div>
            </div>
            
            <div className="modal-footer">
              <LatteButton
                onClick={handleNameModalClose}
                variant="secondary"
                className="btn-secondary flex-1"
              >
                취소
              </LatteButton>
              <LatteButton
                onClick={handleNameSubmit}
                disabled={!name.trim() || consultingLoading}
                className="btn-primary flex-1"
              >
                {consultingLoading ? '시작 중...' : '컨설팅 시작'}
              </LatteButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;